/**
 * Where an item custom field shows (owner ruling 2026-10-15).
 *
 * Each item custom field names one item page tab (or none) and any of three
 * other spots: the item page's right-hand column ("At a glance"), the quick
 * panel, and the item list's default columns. A field with no placement keeps
 * where it has always been: the Assessment tab and nowhere else. A child field
 * (one with a parent) shows wherever its parent does.
 *
 * The one place that answers "which fields show here?" — the item page, the
 * quick panel, the item list and the settings preview all read it.
 */

import type { Attribute, CustomFieldPlacement, CustomFieldPlacementTab } from '@/types/prioritization';

export const DEFAULT_PLACEMENT: CustomFieldPlacement = {
  tab: 'assessment',
  glance: false,
  quickPanel: false,
  listColumn: false,
};

/** The item page's tabs a field can show on, in page order, as the page names them. */
export const PLACEMENT_TABS: { value: CustomFieldPlacementTab; label: string }[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'connections', label: 'Connections' },
  { value: 'assessment', label: 'Assessment' },
  { value: 'prioritization', label: 'Prioritization' },
  { value: 'delivery', label: 'Planning & Delivery' },
  { value: 'none', label: 'No tab' },
];

export type PlacementSpot = 'glance' | 'quickPanel' | 'listColumn';

export const PLACEMENT_SPOTS: { value: PlacementSpot; label: string; hint: string }[] = [
  { value: 'glance', label: 'At a glance', hint: 'The right-hand column of the item page' },
  { value: 'quickPanel', label: 'Quick panel', hint: 'The panel that opens from a row in the item list' },
  { value: 'listColumn', label: 'Item list column', hint: 'Shown by default; people who chose their own columns keep theirs' },
];

/** Is this an item custom field that is switched on? */
/** A custom field someone switched off in Settings → Custom Fields. Other attributes never are. */
export function isSwitchedOffCustomField(a: Pick<Attribute, 'isCustomField' | 'enabled'> | undefined): boolean {
  return a?.isCustomField === true && a.enabled === false;
}

export function isEnabledItemField(a: Attribute): boolean {
  return a.isCustomField === true && a.enabled !== false && (a.objectType ?? 'item') === 'item';
}

/** A field's placement, filled in with today's default for anything unset. */
export function placementOf(field: Pick<Attribute, 'placement'>): CustomFieldPlacement {
  const p = field.placement;
  if (!p) return DEFAULT_PLACEMENT;
  const tab = PLACEMENT_TABS.some((t) => t.value === p.tab) ? p.tab : DEFAULT_PLACEMENT.tab;
  return { tab, glance: p.glance === true, quickPanel: p.quickPanel === true, listColumn: p.listColumn === true };
}

/** Enabled, top-level item fields that show on this tab, in their settings order. */
export function fieldsOnTab(attributes: Attribute[], tab: CustomFieldPlacementTab): Attribute[] {
  return attributes.filter((a) => isEnabledItemField(a) && !a.parentKey && placementOf(a).tab === tab);
}

/** Enabled, top-level item fields that show in this spot. */
export function fieldsInSpot(attributes: Attribute[], spot: PlacementSpot): Attribute[] {
  return attributes.filter((a) => isEnabledItemField(a) && !a.parentKey && placementOf(a)[spot]);
}

/** The places a field shows, in words: "Assessment tab · At a glance". */
export function placementSummary(field: Pick<Attribute, 'placement' | 'parentKey'>, parentLabel?: string): string {
  if (field.parentKey) return `With ${parentLabel ?? 'its parent'}`;
  const p = placementOf(field);
  const parts: string[] = [];
  if (p.tab !== 'none') parts.push(`${PLACEMENT_TABS.find((t) => t.value === p.tab)?.label} tab`);
  for (const s of PLACEMENT_SPOTS) if (p[s.value]) parts.push(s.label);
  return parts.length ? parts.join(' · ') : 'Nowhere yet';
}

/** A field's value as words, for places that show it without its editor. */
export function formatCustomFieldValue(field: Pick<Attribute, 'type'>, value: unknown): string | null {
  if (value == null || value === '') return null;
  if (Array.isArray(value)) return value.length ? value.map(String).join(', ') : null;
  if (field.type === 'boolean') return value === true || value === 'true' ? 'Yes' : 'No';
  if (field.type === 'date' && typeof value === 'string') {
    const d = new Date(value.length === 10 ? `${value}T00:00:00` : value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
  }
  return String(value);
}

/**
 * The item's metadata after one field changes. Changing a parent clears any
 * child value its new value no longer allows.
 */
export function metadataAfterChange(
  meta: Record<string, unknown>,
  fieldKey: string,
  newValue: unknown,
  children: Attribute[],
): Record<string, unknown> {
  const updates: Record<string, unknown> = { ...meta, [fieldKey]: newValue };
  const parentVal = newValue as string | null;
  for (const child of children) {
    const validOpts = parentVal && child.conditionalOptions ? (child.conditionalOptions[parentVal] ?? []) : [];
    const currentChildVal = meta[child.key];
    if (currentChildVal == null || currentChildVal === '') continue;
    const isStillValid = Array.isArray(currentChildVal)
      ? (currentChildVal as string[]).every((v) => validOpts.includes(v))
      : validOpts.includes(currentChildVal as string);
    if (!isStillValid) updates[child.key] = null;
  }
  return updates;
}
